import React from 'react';

import DateFormatter from 'utils/date/DateFormatter';
import DateParser from 'utils/date/DateParser';
import TimeUtil from 'utils/date/TimeUtil';
import DateUtil from 'utils/date/DateUtil';
import AnalyticsUtil from 'utils/AnalyticsUtil';
import { oneDecimalRound } from 'utils/NumberUtils';

import i18next from 'i18next';
import Localizer from 'localizer';
import Translate from 'components/Translate';
import {
    JET_LAG_LEVELS_COLORS,
    JET_LAG_SEVERITY_TYPES,
    JET_LAG_FROM_CALENDAR_QUERY_PARAM,
    LHA_JET_LAG_BODY_CLOCK_SHIFT_FEEDBACK_UNKNOWN,
    LHA_JET_LAG_FEEDBACK_UNKNOWN,
    JET_LAG_CALENDAR_DATE_QUERY_PARAM,
    JET_LAG_GUIDANCE_TYPES
} from './jetLagConstants';

import TimelineChartConstants from 'components/TimelineChart/TimelineChartConstants';

export const getLocalizedDateAndTime = (date, time) => {
    // originDate and destinationDate are Date objects, stored in client timezone
    // => need to take the yyyy-mm-dd format only, to compute the date and time in local time at origin / destination
    const dateString = DateFormatter.formatISODate(DateParser.parseISO(date));
    const timeString = TimeUtil.secondsToHoursMinutesAndSeconds(DateParser.parseTime(time));

    const localizedDateAndTime = DateFormatter.formatConnectTimestamp(new Date(`${dateString}T${timeString}`));
    return localizedDateAndTime;
};

export const getErrorMessages = (flightState, flights, stepNumber, trips) => {
    const {
        originTime,
        originDate,
        originTimezone,
        destinationTime,
        destinationDate,
        destinationTimezone
    } = flightState;

    let errorMessages = {};

    const localizedOriginDate = getLocalizedDateAndTime(originDate, originTime);
    const localizedDestinationDate = getLocalizedDateAndTime(destinationDate, destinationTime);

    const originDateAndTimeInUTC = DateParser.parseISOUTC(localizedOriginDate + originTimezone);
    const destinationDateAndTimeInUTC = DateParser.parseISOUTC(localizedDestinationDate + destinationTimezone);

    const daysDiff = DateFormatter.getDaysDiffBetweenTwoDates(originDate, destinationDate);

    if (daysDiff < 0) {
        errorMessages.destinationDateError = <Translate ns="jet_lag" content="error_message_arrival_date" />;
    } else if (daysDiff === 0 && destinationDateAndTimeInUTC.getTime() < originDateAndTimeInUTC.getTime()) {
        errorMessages.destinationTimeError = <Translate ns="jet_lag" content="error_message_arrival_time" />;
    }

    const previousFlight = flights[stepNumber - 2];
    if (previousFlight) {
        const isBeforePreviousDestination = previousFlight.originDate.getTime() > originDate.getTime();
        const isDuringPreviousDestination =
            previousFlight.originDate.getTime() < originDate.getTime() &&
            originDate.getTime() < previousFlight.destinationDate.getTime();

        if (isBeforePreviousDestination || isDuringPreviousDestination || !previousFlight.originDate) {
            errorMessages.originDateError = (
                <Translate ns="jet_lag" content="error_message_previous_destination_date_conflict" />
            );
        }

        const localizedPreviousDestinationDate = getLocalizedDateAndTime(
            previousFlight.destinationDate,
            previousFlight.destinationTime
        );
        const previousDestinationDateAndTimeInUTC = DateParser.parseISOUTC(
            localizedPreviousDestinationDate + previousFlight.destinationTimezone
        );

        if (
            previousFlight.destinationDate.getTime() === originDate.getTime() &&
            previousDestinationDateAndTimeInUTC.getTime() > originDateAndTimeInUTC.getTime()
        ) {
            errorMessages.originTimeError = (
                <Translate ns="jet_lag" content="error_message_previous_destination_time_conflict" />
            );
        }
    }

    trips.forEach((trip) => {
        // do not check against current trip - this case appears in edit mode
        if (flightState.tripId && trip.tripId === flightState.tripId) {
            return;
        }
        const originFlight = trip.flights[0];
        const destinationFlight = trip.flights[trip.flights.length - 1];
        const tripStartDateInUTC = DateParser.parseISOUTC(originFlight.originTimestamp + originFlight.originTimezone);
        const tripEndDateInUTC = DateParser.parseISOUTC(
            destinationFlight.destinationTimestamp + destinationFlight.destinationTimezone
        );

        //start date of current trip is between start and end date of another trip
        if (
            tripStartDateInUTC.getTime() <= originDateAndTimeInUTC.getTime() &&
            originDateAndTimeInUTC.getTime() <= tripEndDateInUTC.getTime()
        ) {
            errorMessages.originDateError = <Translate ns="jet_lag" content="error_message_another_trip_conflict" />;
        }

        //end date of current trip is between start and end date of another trip
        if (
            tripStartDateInUTC.getTime() <= destinationDateAndTimeInUTC.getTime() &&
            destinationDateAndTimeInUTC.getTime() <= tripEndDateInUTC.getTime()
        ) {
            errorMessages.destinationDateError = (
                <Translate ns="jet_lag" content="error_message_another_trip_conflict" />
            );
        }

        //current trip overlaps with another trip
        if (
            originDateAndTimeInUTC.getTime() <= tripStartDateInUTC.getTime() &&
            tripEndDateInUTC.getTime() <= destinationDateAndTimeInUTC.getTime()
        ) {
            errorMessages.originDateError = <Translate ns="jet_lag" content="error_message_another_trip_conflict" />;
            errorMessages.destinationDateError = (
                <Translate ns="jet_lag" content="error_message_another_trip_conflict" />
            );
        }
    });

    return errorMessages;
};

export const checkFlightFields = (flight) => {
    return !Object.values(flight).includes(null) && !Object.values(flight).includes('');
};

export const validateTimeFormat = (time) => {
    const parts = time.split(/:| /);

    if (isNaN(parts[0]) || isNaN(parts[1])) {
        return false;
    }

    if (parts.length > 2) {
        if (parts.length === 3 && parts[2] !== Localizer.localize('AM') && parts[2] !== Localizer.localize('PM')) {
            return false;
        } else if (
            parts.length === 4 &&
            parts[2] + ' ' + parts[3] !== Localizer.localize('AM') &&
            parts[2] + ' ' + parts[3] !== Localizer.localize('PM')
        ) {
            return false;
        }
    }

    return true;
};

export const isTripInProgress = (trip) => {
    if (!trip || !trip.startDate || !trip.endDate) {
        return false;
    }

    const currentDate = new Date();
    let startDateTime, endDateTime;

    const { flights } = trip;
    if (flights && flights.length > 0) {
        startDateTime = new Date(`${flights[0].originTimestamp}${flights[0].originTimezone}`);
        endDateTime = new Date(
            `${flights[flights.length - 1].destinationTimestamp}${flights[flights.length - 1].destinationTimezone}`
        );
    } else {
        startDateTime = new Date(trip.startDate);
        endDateTime = new Date(trip.endDate);
    }

    return startDateTime.getTime() <= currentDate.getTime() && currentDate.getTime() <= endDateTime.getTime();
};

export const isTripInTheFuture = (trip) => {
    if (!trip || !trip.startDate || !trip.endDate) {
        return false;
    }

    const currentDate = new Date();
    let startDateTime;

    const { flights } = trip;
    if (flights && flights.length > 0) {
        startDateTime = new Date(`${flights[0].originTimestamp}${flights[0].originTimezone}`);
    } else {
        startDateTime = new Date(trip.startDate);
    }

    return startDateTime.getTime() >= currentDate.getTime();
};

export const getJetLagColor = (value) => {
    switch (value) {
        case JET_LAG_SEVERITY_TYPES.SEVERE:
            return JET_LAG_LEVELS_COLORS.severe;
        case JET_LAG_SEVERITY_TYPES.SIGNIFICANT:
            return JET_LAG_LEVELS_COLORS.significant;
        case JET_LAG_SEVERITY_TYPES.MODERATE:
            return JET_LAG_LEVELS_COLORS.moderate;
        case JET_LAG_SEVERITY_TYPES.MILD:
            return JET_LAG_LEVELS_COLORS.mild;
        default:
            return JET_LAG_LEVELS_COLORS.acclimated;
    }
};

export const getFeelsLikeTime = (bodyClockDifference, originTimezone, destinationTimezone) => {
    const nowDate = new Date();
    const nowTimestamp = nowDate.getTime();

    if (originTimezone && destinationTimezone) {
        const timeAtDestination =
            nowTimestamp +
            (parseFloat(destinationTimezone) - parseFloat(originTimezone)) * TimelineChartConstants.MS_IN_ONE_HOUR;
        return DateFormatter.formatTime(DateParser.parseEpoch(timeAtDestination));
    }

    const feelsLikeTimestamp = nowTimestamp + bodyClockDifference * TimelineChartConstants.MS_IN_ONE_HOUR;
    return DateFormatter.formatTime(DateParser.parseEpoch(feelsLikeTimestamp));
};

export const getFeltLikeTime = (bodyClockDifference, originTimezone, destinationTimezone) => {
    const timeDifference =
        originTimezone && destinationTimezone
            ? oneDecimalRound(parseFloat(destinationTimezone) - parseFloat(originTimezone))
            : bodyClockDifference;

    return timeDifference > 0 ? `+${timeDifference}h` : `${timeDifference}h`;
};

export const getJetLagTimeAndLabel = (jetLagDailyStatus) => {
    const { calendarDate, bodyClockDifference, severity, flights } = jetLagDailyStatus;
    const overlappingFlights = jetLagDailyStatus.overlappingFlights || [];

    if (bodyClockDifference === null || severity === 0) {
        return { jetLagTime: '--', jetLagLabel: i18next.t('jet_lag:feels_like') };
    }

    const daysBeforeTrip = getDaysBeforeTrip(jetLagDailyStatus);

    let jetLagTime = '',
        jetLagLabel = '',
        timeDiffVsLocal = 0;

    const dayIsInThePast = DateUtil.isPastDay(calendarDate);
    const timeFunction = dayIsInThePast ? getFeltLikeTime : getFeelsLikeTime;

    // pre-trip
    if (isPreTripDay(jetLagDailyStatus)) {
        const flightsForFirstDay = getFlightsForDaySortedChronologically(flights, calendarDate, daysBeforeTrip);

        if (flightsForFirstDay && flightsForFirstDay.length > 0) {
            const originFlight = flightsForFirstDay[0];
            const destinationFlight = flightsForFirstDay[flightsForFirstDay.length - 1];
            jetLagTime = timeFunction(
                bodyClockDifference,
                originFlight.originTimezone,
                destinationFlight.destinationTimezone
            );
            timeDiffVsLocal = oneDecimalRound(
                parseFloat(destinationFlight.destinationTimezone) - parseFloat(originFlight.originTimezone)
            );
        } else {
            jetLagTime = timeFunction(bodyClockDifference);
            timeDiffVsLocal = bodyClockDifference;
        }

        jetLagLabel = i18next.t('jet_lag:destination');
    }

    // pre-flight
    else if (isPreFlightDay(jetLagDailyStatus)) {
        const nextDayFlights = getFlightsForDaySortedChronologically(flights, calendarDate, 1);

        if (nextDayFlights && nextDayFlights.length > 0) {
            const originFlight = nextDayFlights[0];
            const destinationFlight = nextDayFlights[nextDayFlights.length - 1];

            jetLagTime = timeFunction(
                bodyClockDifference,
                originFlight.originTimezone,
                destinationFlight.destinationTimezone
            );

            timeDiffVsLocal = oneDecimalRound(
                parseFloat(destinationFlight.destinationTimezone) - parseFloat(originFlight.originTimezone)
            );
        } else {
            jetLagTime = timeFunction(bodyClockDifference);
            timeDiffVsLocal = bodyClockDifference;
        }

        jetLagLabel = i18next.t('jet_lag:destination');
    }

    // traveling en-route
    else if (isTravelEnRouteDay(jetLagDailyStatus)) {
        if (overlappingFlights && overlappingFlights.length > 0) {
            const originFlight = overlappingFlights[0];
            const destinationFlight = overlappingFlights[overlappingFlights.length - 1];

            jetLagTime = timeFunction(
                bodyClockDifference,
                originFlight.originTimezone,
                destinationFlight.destinationTimezone
            );

            timeDiffVsLocal = oneDecimalRound(
                parseFloat(destinationFlight.destinationTimezone) - parseFloat(originFlight.originTimezone)
            );
        } else {
            jetLagTime = timeFunction(bodyClockDifference);
            timeDiffVsLocal = bodyClockDifference;
        }

        jetLagLabel = i18next.t('jet_lag:destination');
    }

    // acclimated
    else if (bodyClockDifference === 0 && severity === 1) {
        jetLagTime = i18next.t('jet_lag:acclimated');
        jetLagLabel = dayIsInThePast ? i18next.t('jet_lag:felt_like') : i18next.t('jet_lag:feels_like');
        timeDiffVsLocal = 0;
    }

    // traveling day at destination or at destination
    else {
        jetLagTime = timeFunction(bodyClockDifference);
        jetLagLabel = dayIsInThePast ? i18next.t('jet_lag:felt_like') : i18next.t('jet_lag:feels_like');
        timeDiffVsLocal = bodyClockDifference;
    }

    return { jetLagTime, jetLagLabel, timeDiffVsLocal };
};

export const getNavigationUrl = (path, isFromCalendar) => {
    // if the query string is already included in the path, return the path
    // if the user didn't navigate from calendar, return the regular path
    if (path.indexOf(JET_LAG_FROM_CALENDAR_QUERY_PARAM) >= 0 || !isFromCalendar) {
        return path;
    }

    // path already has a query string
    if (path.indexOf('?') >= 0) {
        return `${path}&${JET_LAG_FROM_CALENDAR_QUERY_PARAM}`;
    }

    // path does not have a query string
    return `${path}?${JET_LAG_FROM_CALENDAR_QUERY_PARAM}`;
};

export const getNavigationUrlWithCalendarDate = (path, isFromCalendar, selectedCalendarDate) => {
    let url = getNavigationUrl(path, isFromCalendar);
    if (isFromCalendar) {
        url = url + `&${JET_LAG_CALENDAR_DATE_QUERY_PARAM}=${selectedCalendarDate}`;
    }

    return url;
};

export const getDaysBeforeTrip = (jetLagDailyStatus) => {
    const { calendarDate, tripStartDate } = jetLagDailyStatus;
    const tripStartDateTimestamp = DateParser.parseISO(tripStartDate).getTime();
    const calendarDateTimestamp = DateParser.parseISO(calendarDate).getTime();
    return Math.floor((tripStartDateTimestamp - calendarDateTimestamp) / TimelineChartConstants.MS_IN_ONE_DAY);
};

export const isPreTripDay = (jetLagDailyStatus) => {
    const daysBeforeTrip = getDaysBeforeTrip(jetLagDailyStatus);
    return daysBeforeTrip > 0;
};
export const isPreFlightDay = (jetLagDailyStatus) => {
    const { calendarDate, flights } = jetLagDailyStatus;
    const nextDayFlights = getFlightsForDaySortedChronologically(flights, calendarDate, 1);
    return !isPreTripDay(jetLagDailyStatus) && nextDayFlights && nextDayFlights.length > 0;
};

export const isTravelDay = (jetLagDailyStatus) => {
    const overlappingFlights = jetLagDailyStatus.overlappingFlights || [];
    return overlappingFlights && overlappingFlights.length > 0;
};

export const isTravelEnRouteDay = (jetLagDailyStatus) => {
    const overlappingFlights = jetLagDailyStatus.overlappingFlights || [];
    const startOfCurrentDay = DateParser.parseISO(jetLagDailyStatus.calendarDate).getTime();

    const nowDate = new Date();
    const isCurrentDate = DateFormatter.getDaysDiff(jetLagDailyStatus.calendarDate) === 0;

    const endOfCurrentDay = isCurrentDate
        ? nowDate.getTime()
        : startOfCurrentDay + TimelineChartConstants.MS_IN_ONE_DAY;
    return (
        overlappingFlights &&
        overlappingFlights.length > 0 &&
        overlappingFlights[overlappingFlights.length - 1].destinationTimestampLocal > endOfCurrentDay
    );
};

export const isTravelDayAndReachedDestination = (jetLagDailyStatus) => {
    const overlappingFlights = jetLagDailyStatus.overlappingFlights || [];
    const startOfCurrentDay = DateParser.parseISO(jetLagDailyStatus.calendarDate).getTime();
    const endOfCurrentDay = startOfCurrentDay + TimelineChartConstants.MS_IN_ONE_DAY;
    return (
        overlappingFlights &&
        overlappingFlights.length > 0 &&
        overlappingFlights[overlappingFlights.length - 1].destinationTimestampLocal < endOfCurrentDay
    );
};

export const isTraveling = (jetLagDailyStatus) => {
    const { calendarDate } = jetLagDailyStatus;
    const isCurrentDate = DateFormatter.getDaysDiff(calendarDate) === 0;
    const overlappingFlights = jetLagDailyStatus.overlappingFlights || [];
    const nowTimestamp = new Date().getTime();
    return (
        isCurrentDate &&
        overlappingFlights &&
        overlappingFlights.length > 0 &&
        nowTimestamp <= overlappingFlights[overlappingFlights.length - 1].destinationTimestampLocal
    );
};

export const isBeforeDestination = (jetLagDailyStatus) => {
    return (
        isPreTripDay(jetLagDailyStatus) || isPreFlightDay(jetLagDailyStatus) || isTravelEnRouteDay(jetLagDailyStatus)
    );
};

export const isUnknownJetLagStatus = (jetLagDailyStatus) => {
    const { bodyClockFeedback, bodyClockShiftFeedback } = jetLagDailyStatus;
    return (
        bodyClockFeedback === LHA_JET_LAG_FEEDBACK_UNKNOWN ||
        bodyClockShiftFeedback === LHA_JET_LAG_BODY_CLOCK_SHIFT_FEEDBACK_UNKNOWN
    );
};

export const getCurrentDaysFlights = (flights, calendarDate) => {
    if (!flights || flights.length === 0) {
        return [];
    }

    const flightsInCurrentTimezone = flights.map((flight) => {
        flight.originTimestampLocal = DateParser.parseISO(
            `${flight.originTimestamp}${flight.originTimezone}`
        ).getTime();
        flight.destinationTimestampLocal = DateParser.parseISO(
            `${flight.destinationTimestamp}${flight.destinationTimezone}`
        ).getTime();

        return flight;
    });

    if (!flightsInCurrentTimezone || flightsInCurrentTimezone.length === 0) {
        return [];
    }

    const startOfCurrentDay = DateParser.parseISO(calendarDate).getTime();
    const endOfCurrentDay = startOfCurrentDay + TimelineChartConstants.MS_IN_ONE_DAY;

    return flightsInCurrentTimezone
        .filter(
            (flight) =>
                (startOfCurrentDay <= flight.originTimestampLocal && flight.originTimestampLocal < endOfCurrentDay) ||
                (startOfCurrentDay <= flight.destinationTimestampLocal &&
                    flight.destinationTimestampLocal < endOfCurrentDay)
        )
        .sort((f1, f2) => f1.originTimestampLocal - f2.originTimestampLocal);
};

export const getFlightsForDaySortedChronologically = (flightsWithLocalTimestamp, calendarDate, daysAfter) => {
    if (!flightsWithLocalTimestamp || flightsWithLocalTimestamp.length === 0) {
        return [];
    }

    const startOfDay = DateParser.parseISO(calendarDate).getTime() + daysAfter * TimelineChartConstants.MS_IN_ONE_DAY;
    const endOfDay = startOfDay + TimelineChartConstants.MS_IN_ONE_DAY;

    return flightsWithLocalTimestamp
        .filter(
            (flight) =>
                (startOfDay <= flight.originTimestampLocal && flight.originTimestampLocal < endOfDay) ||
                (startOfDay <= flight.destinationTimestampLocal && flight.destinationTimestampLocal < endOfDay)
        )
        .sort((f1, f2) => f1.originTimestampLocal - f2.originTimestampLocal);
};

export function pushJetLagEventToAnalytics(action) {
    AnalyticsUtil.push({
        event: 'track_event',
        category: 'Jet Lag Events',
        action: action
    });
}

export const formatLocationString = (location) => {
    return location ? location.split(',')[0] : '';
};

export const getItinerary = (trip) => {
    const { flights } = trip;
    const originFlight = flights[0];
    const destinationFlight = flights[flights.length - 1];

    const originLocation = formatLocationString(originFlight.originLocation);
    const destinationLocation = formatLocationString(
        flights.length === 1 ? originFlight.destinationLocation : destinationFlight.originLocation
    );
    return `${originLocation} - ${destinationLocation}`;
};

export const getDateTimeStringForTrip = (trip) => {
    const { flights } = trip;
    const originFlight = flights[0];
    const destinationFlight = flights[flights.length - 1];

    const parsedOriginDate = DateParser.parseISO(originFlight.originTimestamp);
    const parsedDestinationDate = DateParser.parseISO(destinationFlight.destinationTimestamp);

    return `${DateFormatter.formatDateAndTime(parsedOriginDate)}
     - ${DateFormatter.formatDateAndTime(parsedDestinationDate)}`;
};

export const getCurrentJetLagGuidance = (suggestions) => {
    if (!suggestions || suggestions.length === 0) {
        return null;
    }

    const nowDate = DateParser.parseISO();
    const nowTimestamp = nowDate.getTime();

    const suggestionsForNow = suggestions.filter((suggestion) => {
        const { guidanceStartTime, guidanceEndTime } = suggestion;
        const startTimestamp = DateParser.parseISO(guidanceStartTime).getTime();
        const endTimestamp = DateParser.parseISO(guidanceEndTime).getTime();

        return startTimestamp <= nowTimestamp && nowTimestamp <= endTimestamp;
    });

    if (suggestionsForNow.length === 0) {
        return <Translate ns="jet_lag" content="no_guidance" />;
    }

    if (suggestionsForNow.length === 1) {
        return <Translate ns="jet_lag" content={suggestionsForNow[0].guidanceType} />;
    }

    // The only combinations that are still possible are:
    // Sleep and avoid light
    // Seek light and be active
    const types = suggestionsForNow.map((suggestion) => suggestion.guidanceType);

    if (types.length !== 2) {
        return null; // impossible state
    }

    const sleepAndAvoidLight = [
        JET_LAG_GUIDANCE_TYPES.LHA_JET_LAG_NOTIFICATION_TYPE_SLEEP.type,
        JET_LAG_GUIDANCE_TYPES.LHA_JET_LAG_NOTIFICATION_TYPE_LIGHT_AVOID.type
    ];
    if (sleepAndAvoidLight.includes(types[0]) && sleepAndAvoidLight.includes(types[1])) {
        return <Translate ns="jet_lag" content="sleep_and_avoid_light_guidance" />;
    }

    const seekLightAndBeActive = [
        JET_LAG_GUIDANCE_TYPES.LHA_JET_LAG_NOTIFICATION_TYPE_LIGHT_EXPOSE.type,
        JET_LAG_GUIDANCE_TYPES.LHA_JET_LAG_NOTIFICATION_TYPE_ACTIVITIES.type
    ];

    if (seekLightAndBeActive.includes(types[0]) && seekLightAndBeActive.includes(types[1])) {
        return <Translate ns="jet_lag" content="seek_light_and_be_active_guidance" />;
    }

    // no other possible states
    return null;
};
